import { useSyncExternalStore } from 'react';
import type { UserStatus } from '@/types/api';

/** The statuses the shell's account menu offers; OFFLINE is shown, never chosen. */
export type SelectablePresence = 'ONLINE' | 'IDLE' | 'DND' | 'INVISIBLE';
const SELECTABLE: readonly SelectablePresence[] = ['ONLINE', 'IDLE', 'DND', 'INVISIBLE'];

export function selectablePresence(value: unknown): SelectablePresence | null {
  return SELECTABLE.includes(value as SelectablePresence) ? (value as SelectablePresence) : null;
}

/** How Bunker talks to the shell that shows its status: the platform frame or the Desktop bridge. */
export interface PresenceChannel {
  /** Bunker's confirmed own status; `error` when a change the shell asked for did not take. */
  report(status: UserStatus, error?: 'update_failed'): void;
  /** The shell asks for a status. Returns the unsubscribe. */
  onSet(cb: (status: SelectablePresence) => void): () => void;
  /** The shell says it shows the status (its account menu). Returns the unsubscribe. */
  onHosted(cb: () => void): () => void;
}

/*
  Whether the shell around Bunker shows the status. Until it says so Bunker keeps its own status
  choice in Settings: a shell without the account-menu status (an older build, the order of a
  release) must not leave the person without a way to set it.
*/
let hosted = false;
const hostedListeners = new Set<() => void>();
function markHosted(): void {
  if (hosted) return;
  hosted = true;
  hostedListeners.forEach((cb) => cb());
}

/** True once the shell has said it shows the status; Bunker then hides its own choice. */
export function useShellHostsPresence(): boolean {
  return useSyncExternalStore(
    (cb) => { hostedListeners.add(cb); return () => hostedListeners.delete(cb); },
    () => hosted,
  );
}

/** For tests. */
export function resetShellPresenceForTest(): void {
  hosted = false;
}

export interface PresenceDeps {
  /** The signed-in user's status now, or null before sign-in. */
  current(): UserStatus | null;
  /** Calls back on every change of the signed-in user's status. Returns the unsubscribe. */
  subscribe(cb: () => void): () => void;
  /** Applies a status permanently (cancels a timed one); throws when it did not take. */
  set(status: SelectablePresence): Promise<void>;
}

/**
 * Owner's decision (19.10.2026): everything about the account is in the shell, the presence
 * status too (in its account menu); Bunker stays the owner of the status. Bunker tells the shell
 * its confirmed status — on start, on every change, also from another device — and applies the
 * one the shell asks for through its own API, then reports the result. Nothing optimistic: a
 * change that did not take reports the old status with `update_failed`.
 */
export function installShellPresence(channel: PresenceChannel, deps: PresenceDeps): () => void {
  let reported: UserStatus | null = null;
  const report = (error?: 'update_failed') => {
    const status = deps.current();
    if (!status) return;
    if (!error && status === reported) return;
    reported = status;
    channel.report(status, error);
  };
  report();
  const offStore = deps.subscribe(() => report());
  const offHosted = channel.onHosted(() => { markHosted(); reported = null; report(); });
  const offSet = channel.onSet((status) => {
    const current = deps.current();
    // No signed-in user (also: another account's session the frame refused) — nothing to change.
    if (!current) return;
    if (status === current) {
      reported = null;
      report();
      return;
    }
    void deps.set(status).then(() => report(), () => report('update_failed'));
  });
  return () => { offStore(); offSet(); offHosted(); };
}
